import { useCallback, useMemo } from 'react';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { PreviewTable } from '../../../../components/PreviewTable';
import { DateCell } from '../../../../components/table/DateCell';
import { TextCell } from '../../../../components/table/TextCell';
import { evidenceReceiptsApi } from '../evidenceApi';
import type { EvidenceReceiptListItem } from '../evidenceTypes';
import { personnelLabel, usePersonnelNames } from '../../../human-resources/personnel/useStaff';

/**
 * A case's ACKNOWLEDGEMENT RECEIPTS tab — product call, 08 Oct 2026: *the standard create/search
 * index table*. The server's list, scoped to the case, searched by receipt or inventory
 * number; Generate in the toolbar's leading slot.
 *
 * <b>No row kebab beyond Other Info</b>: a receipt is neither edited nor deleted. Its anchor opens
 * a PREVIEW listing the items it acknowledges — there is no receipt page to navigate to.
 */
export function EvidenceReceiptsPanel({
  caseId,
  reloadKey,
  onCreate,
  createDisabledReason,
}: {
  caseId: string;
  reloadKey: number;
  onCreate: () => void;
  createDisabledReason: string | null;
}) {
  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) =>
      evidenceReceiptsApi.list(caseId, { ...query, sort: query.sort ?? undefined }, signal),
    // reloadKey re-creates the callback, which is what makes the table refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseId, reloadKey],
  );

  const state = useDataTable<EvidenceReceiptListItem>({ fetchPage, pageSize: 25 });

  // The people on the page on screen, resolved in ONE batch — the PII seam's rule.
  const personnelIds = useMemo(
    () => state.rows.flatMap((r) => [r.receivedFromPersonnelId, r.receivedByPersonnelId]),
    [state.rows],
  );
  const { names, canRead } = usePersonnelNames(personnelIds);

  const columns = useMemo<DataColumn<EvidenceReceiptListItem>[]>(
    () => [
      {
        key: 'receiptNumber',
        header: 'AR No.',
        width: 150,
        sortable: true,
        render: (row) => <TextCell value={row.receiptNumber} mono />,
      },
      {
        key: 'receivedFrom',
        header: 'Received from',
        width: 200,
        summary: true,
        render: (row) => <TextCell value={personnelLabel(row.receivedFromPersonnelId, names, canRead)} />,
      },
      {
        key: 'receivedBy',
        header: 'Received by',
        width: 200,
        summary: true,
        render: (row) => <TextCell value={personnelLabel(row.receivedByPersonnelId, names, canRead)} />,
      },
      {
        key: 'receivedAt',
        header: 'Received at',
        width: 200,
        sortable: true,
        summary: true,
        render: (row) => <DateCell value={row.receivedAt} />,
      },
      {
        // A MANY-TO-MANY joined on one line — searchable server-side, never sortable.
        key: 'items',
        header: 'Items',
        width: 320,
        render: (row) => <TextCell value={row.items.map((i) => i.inventoryNumber).join(', ')} mono />,
      },
    ],
    [names, canRead],
  );

  return (
    <DataTablePanel
      id="evidence-case-receipts"
      state={state}
      columns={columns}
      rowKey={(row) => row.id}
      searchPlaceholder="Search receipt or inventory no."
      fill
      rowPreview={{
        title: 'Acknowledgement receipt',
        // The Share dialog's width — the items table needs it (product call, 08 Oct 2026).
        size: '3xl',
        details: (row) => (
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-slate-700">Items ({row.items.length})</p>
            <PreviewTable
              variant="table-display"
              rows={row.items.map((i) => ({
                label: i.inventoryNumber,
                value: `${i.typeName} — ${i.description}`,
              }))}
            />
          </div>
        ),
      }}
      onCreate={onCreate}
      createLabel="Generate"
      createPermission="evidence-receipts.create"
      createDisabledReason={createDisabledReason}
      otherInfo={(row) => row}
      emptyMessage="No acknowledgement receipt has been generated for this case."
      emptySearchMessage="No receipt matches that search."
    />
  );
}
